import React, { useState, useEffect, useCallback } from 'react'
import bindAllActions from '../../common/bindAllActions'
import { getUrlQueryParam, isLoggedIn, isDev } from '../../common/ambient'
import { Loader } from '../../components'
import { getThemeObj } from '../../common/skinner'
// import PropTypes from 'prop-types';

// Debug flag to simulate successful verification (only works in dev mode)
// Set this to true to force verification to succeed without server call
const DEBUG_FORCE_SUCCESS = false;

function UaPage ({ match, location, history, user, social, socialActions, userActions }) {
  const { id } = match.params
  const { userChecked } = user
  const { getUserActionPending, patchUserActionPending, addUserActionPending } = social
  const [ua, setUa] = useState(null)
  const code = getUrlQueryParam(location, 'code')
  const verifyCode = getUrlQueryParam(location, 'verifyCode')
  const [codeInput, setCodeInput] = useState(verifyCode || code)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(false)
  const [autoVerifying, setAutoVerifying] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState(null) // null, 'verifying', 'success', 'fail'
  const [errorMessage, setErrorMessage] = useState(null)
  
  useEffect(() => {
    if (secondsLeft === false) return
    if (secondsLeft > 1) {
      setTimeout(() => {
        setSecondsLeft(secondsLeft - 1)
      }, 1000)
    } else {
      userActions.logoutUser()
    }
  }, [secondsLeft, userActions])
  
  // "init" - after checking login
  useEffect(() => {
    if (code && userChecked && (!getUserActionPending && (!ua || ua._id !== id))) {
      socialActions.getUserAction(id)
        .then(res => {
          if (!res) throw new Error('no auth')
          // delete-account must be logged in
          if (res.type === 'delete-account' && !isLoggedIn(user)) {
            history.push('/')
          }
          if (res.type === 'multi-sms-link' 
            || res.type === 'multi-sms-verification'
            || res.type === 'alert-set-group-member-sms'
            || res.type === 'alert-set-group-member-email'
          ) {
            if (res.data.code !== parseInt(code)) {
              history.push('/')
            }
            
            // Set the verifyCode if available in URL
            const urlVerifyCode = getUrlQueryParam(location, 'verifyCode')
            if (urlVerifyCode) {
              setCodeInput(urlVerifyCode)
              setAutoVerifying(true)
            } else {
              setCodeInput('')
            }
          }
          setUa(res)
        })
        .catch(err => {
          console.log(err)
          history.push('/')
        })
    }
  }, [userChecked, user, history, code, socialActions, getUserActionPending, ua, setUa, id, location])

  // Helper function to handle verification
  const handleVerification = useCallback((code) => {
    setVerificationStatus('verifying')

    // Check for debug flag to force success
    if (isDev() && DEBUG_FORCE_SUCCESS) {
      // Simulate a delay for more realistic testing
      setTimeout(() => {
        setVerificationStatus('success')
        setName(true)
      }, 1500)
      return
    }

    // Normal execution - call the API
    const updateData = {
      code: ua.data.code,
      verifiedCode: code
    };

    return socialActions.patchUserAction(id, { data: Object.assign({}, ua.data, updateData) })
      .then(res => {
        console.log('res', res)
        if (res.result.ok) {
          setVerificationStatus('success')
          setName(true)
          setErrorMessage(null)
        } else {
          setVerificationStatus('fail')
          setName('fail')
          setErrorMessage(res.result.message || 'Sorry, that code was incorrect. Please try again.')
        }
        return res
      })
      .catch(err => {
        setVerificationStatus('fail')
        setName('fail')
        setErrorMessage(err.message || 'Sorry, that code was incorrect. Please try again.')
        throw err
      })
  }, [ua, socialActions, id])

  // Handle auto verification when verifyCode is in URL
  useEffect(() => {
    if (ua && autoVerifying && verifyCode && !verificationStatus) {
      // Check if this is one of the verification types
      if (ua.type === 'multi-sms-verification' ||
          ua.type === 'alert-set-group-member-sms' ||
          ua.type === 'alert-set-group-member-email') {
        handleVerification(verifyCode)
      }
    }
  }, [ua, autoVerifying, verifyCode, verificationStatus, handleVerification])

  const btnOrLoader = (btn) => {
    if (getUserActionPending || patchUserActionPending || addUserActionPending) {
      return <Loader />
    }
    return btn
  }
  
  let title = '...'
  let body = <Loader />
  if (ua) {
    title = 'Verify Account Deletion'
    body = (
      <div className='verify-form'>
        <p>Please enter the code from your email to delete your account.</p>
        <input type='text' value={codeInput} onChange={evt => setCodeInput(evt.target.value)} /> <button className='btn btn-primary' onClick={() => {
          if (window.confirm('Are you sure?')) {
            socialActions.patchUserAction(id, { data: Object.assign({}, ua.data, { verifyCode: codeInput }) })
            setSecondsLeft(3)
          }
        }}>Delete</button>
      </div>
    )
    if (ua.type === 'multi-sms-link') {
      title = 'Verify your SMS number'
      body = (
        <div className='verify-form'>
          <p>An AWN user wants to add you to their account. Please use the form below to verify the phone number you would like to use to recieve SMS alerts.</p>
          <label>Your Name</label>
          <input type='text' value={name} onChange={evt => setName(evt.target.value)} />
          <label>Phone Number</label>
          <input type='text' value={phone} onChange={evt => setPhone(evt.target.value)} /><br/>
          {btnOrLoader(
            <button className='btn btn-primary' disabled={!(name && phone.length >= 10)} onClick={() => {
              const data = {
                userId: ua.userId,
                type: 'multi-sms-verification',
                data: {
                  name,
                  phone: phone.replace(/[^0-9]/g, ''),
                  code: Math.floor(Math.random() * 10000),
                  verifyCode: Math.floor(Math.random() * 10000),
                  userActionId: ua._id
                }
              }
              socialActions.addUserAction(data)
                .then(res => {
                  history.push(`/common/ua/${res._id}?code=${data.data.code}`)
                })
            }}>Send SMS Code</button>
          )}
        </div>
      )
    } else if (
      ua.type === 'multi-sms-verification'
      || ua.type === 'alert-set-group-member-sms'
      || ua.type === 'alert-set-group-member-email'
    ) {
      title = ua.type === 'alert-set-group-member-email' ? 'Verify your Email address' : 'Verify your SMS number'
      
      if (verificationStatus === 'verifying') {
        body = (
          <div className='verify-form verifying'>
            <Loader />
            <p>Verifying your code...</p>
          </div>
        )
      } else if (name === true || verificationStatus === 'success') {
        title = 'Welcome!'
        body = (
          <div className='verify-form success'>
            <div className="check-icon">
              <img src={require('../../assets/common/check-blue.svg')} alt="Success" />
            </div>
            <h2>You're all set!</h2>
            <h3>You don't need an account to receive alerts.</h3>
            <p>The group admin can now assign weather alert sets to you,
            and you will get notifications directly to your {ua.type === 'alert-set-group-member-email' 
              ? 'email inbox.' 
              : 'mobile phone.'}</p>
          </div>
        )
      } else if (name === 'fail' || verificationStatus === 'fail') {
        body = (
          <div className='verify-form fail'>
            <p>{errorMessage || 'Sorry, that code was incorrect. Please try again.'}</p>
            {!errorMessage || errorMessage.indexOf('valid') === -1 ? (
              <>
                <input type='text' value={codeInput} onChange={evt => setCodeInput(evt.target.value)} />
                {btnOrLoader(
                  <button className='btn btn-primary' onClick={() => handleVerification(codeInput)}>
                    Verify
                  </button>
                )}
              </>
            ) : null}
          </div>
        )
      } else {
        body = (
          <div className='verify-form'>
            <p>{ua.type === 'alert-set-group-member-email' 
              ? 'Please enter the code from your email to verify your email address.' 
              : 'Please enter the code from your SMS to verify your phone number.'}</p>
            <input type='text' value={codeInput} onChange={evt => setCodeInput(evt.target.value)} /> 
            {btnOrLoader(
              <button className='btn btn-primary' onClick={() => handleVerification(codeInput)}>
                Verify
              </button>
            )}
          </div>
        )
      }
    }
    if (secondsLeft > 0) {
      body = (
        <div className='verify-form'>
          <p>Thanks for using {getThemeObj().sitename}!</p>
          <p>We'll <a onClick={userActions.logoutUser}>log you out</a> in {secondsLeft}.</p>
        </div>
      )
    }
  }
  return (
    <div className="common-ua-page centered-verification">
      <div className='page-body'>
        <h1>
          {title}
        </h1>
        {body}
      </div>
    </div>
  )
};

export default bindAllActions(UaPage)

UaPage.propTypes = {};
UaPage.defaultProps = {};

UaPage.displayName = 'UaPage'